import type { MoneyAmount, TrendDirection } from '@console/types'

export function formatMoney(amount: MoneyAmount): string | null {
  if (amount === null) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: amount.currency,
    currencyDisplay: 'code',
    maximumFractionDigits: 0,
  }).format(amount.value)
}

const TREND_ARROW: Record<TrendDirection, string> = { up: '↑', down: '↓', same: '→' }

/**
 * `delta` arrives signed; the arrow already carries the direction, so the
 * number reads as a magnitude, not "↑ -12."
 */
export function formatTrend(trend: TrendDirection, delta: number, unit = ''): string {
  if (trend === 'same') return `${TREND_ARROW.same} same`
  return `${TREND_ARROW[trend]} ${String(Math.abs(delta))}${unit}`
}

/**
 * A duration in milliseconds — not an ISO timestamp run through
 * `date-fns`'s `formatDistanceToNow`, which always measures against the
 * browser's real clock. The backend's own notion of "now" for this fixture
 * dataset is the dataset's own latest event (`aggregates.ts`,
 * `datasetAnchorNow`), not real wall-clock time, so the age it hands the
 * frontend has to be rendered as-is, not recomputed from a raw date.
 */
export function formatAgeMs(ms: number): string {
  const minutes = Math.round(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${String(minutes)} min`

  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${String(hours)} hr`

  const days = Math.round(hours / 24)
  return `${String(days)} day${days === 1 ? '' : 's'}`
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE INSTANT, IN THE TIMEZONE IT CLAIMS — `LGH-024`.
 *
 * ── THE BUG THIS EXISTS TO FIX ────────────────────────────────────────────
 * Every "as of … UTC" on this console was **local time wearing a UTC label**.
 * `date-fns`' `format()` renders in the browser's timezone; the `'UTC'` in the
 * pattern was a literal, not a conversion:
 *
 *     instant             2026-08-31T22:19:00Z
 *     format(…, "'UTC'")  "1 Sep 2026, 02:19 UTC"     ← on a UTC+4 machine
 *
 * **Four hours out, a day out, and asserting the timezone it is not in.** The
 * machine this was found on is UTC+4, so it was wrong on every screen that
 * renders an as-of — which is all of them.
 *
 * That matters more here than it would elsewhere. This console's subject is
 * *when a thing was true*: `R-075` was ruled hours ago as **"a claim whose
 * truth depends on when it was evaluated carries that time"**, and a receipt
 * exists to be checked against the world later. A timestamp that is confidently
 * mislabelled is the same defect the ruling is about, one layer down.
 *
 * ── WHY `Intl` AND NOT A LIBRARY ──────────────────────────────────────────
 * `Intl.DateTimeFormat` takes an explicit `timeZone`, so the conversion is
 * done rather than asserted, and it needs no new dependency. `date-fns-tz`
 * would also work and is a package for one call.
 *
 * Returns the original string when it cannot be parsed: an unrenderable
 * timestamp is still evidence, and dropping it would hide something the
 * platform did send.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const UTC_INSTANT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function formatInstant(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  // en-GB gives "31 Aug 2026, 22:19"; the label is appended rather than
  // formatted in, because now it is true.
  return `${UTC_INSTANT.format(parsed)} UTC`
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * *SAME DAY* MEANS THE TENANT'S DAY — `R-079`, `LGH-025`.
 *
 * `LGH-018`'s founder ruling renders an as-of as **the time alone when it is
 * from today, and with the date when it is older**. `LGH-025` asks the
 * question that rule does not answer: **today according to whom?**
 *
 *   At 01:00 in Dubai, a draft from 21:00 UTC yesterday is **today** locally
 *   and **yesterday** in UTC. The rule gives opposite answers.
 *
 * `R-079` settles it: **the tenant declares a timezone; display converts at
 * the edge and never in the record.** So the calendar is the business's, not
 * the reader's.
 *
 * ── THE BROWSER'S TIMEZONE IS NEVER READ, AND THAT IS THE POINT ────────────
 * Nothing here calls `getTimezoneOffset`, `toLocaleString` without a zone, or
 * any other ambient-local API. **The operator's laptop is not the business.**
 * An operations manager in Dubai reviewing from a machine still set to London
 * must see the business's day, and `format.test.ts` pins that by asserting
 * output this machine's own offset (+4) would falsify.
 *
 * `LGH-024` is why that is stated rather than assumed: every "as of … UTC" on
 * this console was the browser's local time with a UTC label, for exactly this
 * reason.
 *
 * ── WHEN THE TIMEZONE IS NOT SERVED, IT REFUSES RATHER THAN DEFAULTS ───────
 * `PLT-046` carries the instruction `DR-017` did not: **an undeclared timezone
 * refuses rather than defaults** — *"probably Dubai is not established to be
 * Dubai."*
 *
 * A renderer cannot refuse to render, so it refuses the **claim** instead:
 * with no zone it returns the full explicit instant in UTC and never the
 * time-alone form. **Time alone is the form that asserts "this is today"**,
 * and that assertion needs a calendar nobody has supplied. Degrading to the
 * more explicit rendering is the honest direction — the reader loses brevity,
 * not truth.
 *
 * **`R-067` holds either way:** converting a served instant for display is
 * presentation; deciding which day it belongs to without being told would be
 * classification.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function formatAsOf(
  iso: string,
  options: { tenantTimeZone?: string | undefined; now?: Date } = {},
): string {
  const { tenantTimeZone, now = new Date() } = options
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso

  // No declared calendar — the explicit form, never "today".
  if (tenantTimeZone === undefined || tenantTimeZone === '') return formatInstant(iso)

  let dayOf: (d: Date) => string
  let timeOnly: Intl.DateTimeFormat
  let full: Intl.DateTimeFormat
  try {
    dayOf = (d) => new Intl.DateTimeFormat('en-CA', { timeZone: tenantTimeZone }).format(d)
    timeOnly = new Intl.DateTimeFormat('en-GB', {
      timeZone: tenantTimeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      // `shortOffset` ("GMT+4"), not `short` ("GST"). An abbreviation is a
      // thing the reader has to already know, and `LGH-024` was a whole
      // session about a timezone label nobody could check. An offset is
      // unambiguous to anyone.
      timeZoneName: 'shortOffset',
    })
    full = new Intl.DateTimeFormat('en-GB', {
      timeZone: tenantTimeZone,
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'shortOffset',
    })
    // `Intl` throws on an unknown zone only when first used, not at construction.
    dayOf(parsed)
  } catch {
    // A zone the runtime does not know is not a calendar either. Same refusal.
    return formatInstant(iso)
  }

  return dayOf(parsed) === dayOf(now) ? timeOnly.format(parsed) : full.format(parsed)
}
